import { Fragment, useState } from "react";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { BellIcon, MenuIcon, XIcon } from "@heroicons/react/outline";
import { useRouter } from "next/router";
import { app, auth, db, useCurrentAuth } from "@/utils/FirebaseProvider";
import { useSignOut } from "react-firebase-hooks/auth";
import { SidebarComponent } from "../SideBar/SidebarComponent";
import Head from "next/head";
import { ProjectSideBar } from "../ProjectSideBar/ProjectSideBar";

import { ChevronDownIcon } from "@heroicons/react/solid";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { getFunctions } from "firebase/functions";
import { signInWithCustomToken } from "firebase/auth";
import { useCurrentProfile } from "@/utils/useCurrentProfile";
import { RotatingSquare } from "react-loader-spinner";
import { useGetPublicUserTenants } from "@/utils/useGetPublicUserTenants";
import clsx from "clsx";
import { useDocumentDataOnce } from "react-firebase-hooks/firestore";
import { publicUserConverter } from "@/pages/public_users/[public_usersId]";
import { doc } from "firebase/firestore";
import { Modal } from "../Modal/Modal";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export const MainMenu = (props: any) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profile] = useCurrentAuth();
  const [loading, setLoading] = useState(false);
  const [user] = useCurrentProfile();
  const [signOut] = useSignOut(auth);
  const router = useRouter();
  const tenants = useGetPublicUserTenants();
  const [executeCallable] = useHttpsCallable(
    getFunctions(app, "europe-west1"),
    "changeTenant"
  );
  const [publicUser] = useDocumentDataOnce(
    doc(db, `/public_users/${profile?.email}`).withConverter(
      publicUserConverter
    )
  );

  const loginPage = router.asPath === "/";
  return !profile || loginPage ? (
    props.children
  ) : (
    <div className="">
      <div>
        <Head>
          <title>Actproof</title>
          <meta
            name="viewport"
            content="initial-scale=1.0, width=device-width"
          />
        </Head>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-40 flex md:hidden print:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative flex w-full max-w-xs flex-1 flex-col bg-[#1F2937]">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="h-0 flex-1 overflow-y-auto pt-5 pb-4">
                  <div className="flex shrink-0 items-center px-4">
                    <img
                      className="h-8 w-auto"
                      src="/assets/images/actproof.png"
                      alt="ActProof"
                    />
                  </div>
                  <nav className={clsx("mt-5 space-y-1 px-2")}>
                    <SidebarComponent />
                  </nav>
                </div>
              </div>
            </Transition.Child>
            <div className="w-14 shrink-0">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="z-20 hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col print:hidden">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex min-h-0 flex-1 flex-col  bg-[#1F2937]">
            {/* <div className="flex min-h-0 flex-1 flex-col  bg-gradient-to-b from-hred-300 via-hred-100 to-horange-100 "> */}
            <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
              <div className="flex shrink-0 items-center px-6">
                <svg
                  width="100"
                  height="41"
                  viewBox="0 0 100 41"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.12083 12.0741C1.54072 13.5729 9.28555e-05 20.4576 0 21.9242C-9.28547e-05 23.3909 15.9437 27.6934 18.6813 24.125C21.4189 20.5566 10.701 10.5752 6.12083 12.0741Z"
                    fill="#EAA25A"
                  />
                  <path
                    d="M30.2846 27.8855C29.0035 30.0613 21.7351 32.2722 15.365 30.8184C15.365 30.8184 11.6119 30.1435 8.15987 28.5867C4.78559 27.065 -1.08556 22.7209 0.253796 23.3585C1.59315 23.9961 7.58561 26.3551 12.1767 26.3551C16.7677 26.3551 18.6422 25.7815 19.2543 25.2714C19.8664 24.7614 21.3584 23.2469 18.4892 18.0029C15.6201 12.7589 7.07641 11.117 7.07641 11.117C7.07641 11.117 7.52314 10.2194 10.2006 9.70928C12.878 9.19921 18.4728 11.3402 21.6767 15.1336C24.8806 18.927 24.2267 23.9058 25.1835 23.9961C26.1403 24.0864 26.6503 23.61 27.4792 22.7798C28.3081 21.9495 31.1134 26.4778 30.2846 27.8855Z"
                    fill="#EAA25A"
                  />
                  <path
                    d="M26.0781 16.7757C24.0378 12.9502 20.9729 11.5071 17.4707 10.5273C17.4707 10.5273 23.0679 14.0978 24.1654 17.5301C25.2628 20.9623 25.058 22.6415 25.6318 22.8328C26.2056 23.024 27.162 21.9932 27.4171 21.4938C27.6721 20.9945 28.1184 20.6012 26.0781 16.7757Z"
                    fill="#EAA25A"
                  />
                  <path
                    d="M42.4487 16.6347L39.7241 25.4609H36.8306L40.9367 14.2862H42.7711L42.4487 16.6347ZM44.7052 25.4609L41.9729 16.6347L41.6198 14.2862H43.4772L47.6063 25.4609H44.7052ZM44.5977 21.2934V23.3733H38.8645V21.2934H44.5977ZM51.8122 23.6189C52.0527 23.6189 52.2574 23.5754 52.4262 23.4885C52.6002 23.3964 52.7306 23.2684 52.8176 23.1047C52.9097 22.9359 52.9532 22.7338 52.9481 22.4984H55.3811C55.3862 23.1226 55.2327 23.6701 54.9206 24.1408C54.6136 24.6065 54.194 24.9697 53.6619 25.2307C53.1349 25.4865 52.5388 25.6144 51.8736 25.6144C51.2238 25.6144 50.6558 25.5095 50.1698 25.2998C49.6837 25.0849 49.2769 24.7881 48.9494 24.4095C48.6271 24.0257 48.384 23.578 48.2203 23.0663C48.0617 22.5547 47.9824 21.9995 47.9824 21.4009V21.2243C47.9824 20.6257 48.0617 20.0705 48.2203 19.5589C48.384 19.0421 48.6271 18.5944 48.9494 18.2157C49.2769 17.832 49.6811 17.5352 50.1621 17.3255C50.6482 17.1106 51.2136 17.0031 51.8583 17.0031C52.549 17.0031 53.1579 17.1336 53.6849 17.3945C54.217 17.6555 54.634 18.0341 54.9359 18.5304C55.2378 19.0267 55.3862 19.6279 55.3811 20.334H52.9481C52.9532 20.0833 52.9148 19.8582 52.833 19.6586C52.7511 19.4591 52.6232 19.3005 52.4492 19.1828C52.2804 19.06 52.0629 18.9986 51.7969 18.9986C51.5308 18.9986 51.3159 19.0574 51.1522 19.1751C50.9884 19.2928 50.8631 19.4565 50.7761 19.6663C50.6942 19.871 50.6379 20.1064 50.6072 20.3724C50.5816 20.6385 50.5689 20.9225 50.5689 21.2243V21.4009C50.5689 21.7079 50.5816 21.997 50.6072 22.2681C50.6379 22.5342 50.6968 22.7696 50.7838 22.9742C50.8707 23.1738 50.9961 23.3324 51.1598 23.4501C51.3236 23.5627 51.541 23.6189 51.8122 23.6189ZM60.861 17.1566V18.9525H55.8569V17.1566H60.861ZM56.9084 15.092H59.4949V22.7517C59.4949 22.9717 59.5204 23.1431 59.5716 23.2659C59.6228 23.3836 59.7098 23.468 59.8326 23.5192C59.9605 23.5652 60.1293 23.5882 60.3391 23.5882C60.4875 23.5882 60.6103 23.5857 60.7075 23.5806C60.8047 23.5703 60.8968 23.5575 60.9838 23.5422V25.3919C60.7638 25.4635 60.5335 25.5172 60.2931 25.553C60.0526 25.594 59.7967 25.6144 59.5256 25.6144C58.973 25.6144 58.4997 25.5275 58.1057 25.3535C57.7168 25.1795 57.4201 24.9058 57.2154 24.5323C57.0107 24.1587 56.9084 23.6727 56.9084 23.074V15.092ZM64.499 18.753V28.6537H61.9125V17.1566H64.3224L64.499 18.753ZM69.5951 21.209V21.3702C69.5951 21.9739 69.5261 22.5342 69.3879 23.051C69.2498 23.5678 69.0451 24.018 68.7739 24.4018C68.5027 24.7855 68.165 25.0849 67.7608 25.2998C67.3617 25.5095 66.8987 25.6144 66.3717 25.6144C65.8753 25.6144 65.4481 25.5044 65.0899 25.2844C64.7318 25.0644 64.4324 24.76 64.192 24.3711C63.9515 23.9822 63.757 23.5371 63.6087 23.0356C63.4603 22.5342 63.3426 22.0046 63.2556 21.4469V21.2397C63.3426 20.641 63.4603 20.0859 63.6087 19.5742C63.757 19.0574 63.9515 18.6072 64.192 18.2234C64.4324 17.8397 64.7292 17.5404 65.0823 17.3255C65.4404 17.1106 65.8677 17.0031 66.364 17.0031C66.891 17.0031 67.3541 17.1029 67.7532 17.3024C68.1574 17.502 68.4951 17.7885 68.7662 18.162C69.0425 18.5304 69.2498 18.973 69.3879 19.4898C69.5261 20.0066 69.5951 20.5796 69.5951 21.209ZM67.0087 21.3702V21.209C67.0087 20.8866 66.9857 20.5924 66.9396 20.3264C66.8936 20.0552 66.8194 19.8198 66.717 19.6203C66.6198 19.4207 66.4893 19.2672 66.3256 19.1598C66.1619 19.0523 65.9598 18.9986 65.7193 18.9986C65.443 18.9986 65.2102 19.0421 65.0209 19.1291C64.8367 19.2109 64.6883 19.3389 64.5757 19.5128C64.4683 19.6817 64.3915 19.894 64.3455 20.1498C64.3045 20.4006 64.2866 20.6948 64.2917 21.0325V21.6618C64.2815 22.0507 64.3199 22.3935 64.4069 22.6903C64.4938 22.9819 64.6448 23.2096 64.8597 23.3733C65.0746 23.5371 65.3662 23.6189 65.7346 23.6189C65.9802 23.6189 66.1849 23.5627 66.3486 23.4501C66.5124 23.3324 66.6428 23.1712 66.7401 22.9666C66.8373 22.7568 66.9063 22.5163 66.9473 22.2451C66.9882 21.9739 67.0087 21.6823 67.0087 21.3702ZM73.3559 19.2135V25.4609H70.7694V17.1566H73.187L73.3559 19.2135ZM75.8119 17.0952L75.7658 19.4975C75.6635 19.4872 75.5254 19.4744 75.3514 19.4591C75.1774 19.4437 75.0316 19.4361 74.9139 19.4361C74.612 19.4361 74.3511 19.4719 74.1311 19.5435C73.9162 19.61 73.7371 19.7124 73.5938 19.8505C73.4557 19.9836 73.3533 20.1524 73.2868 20.3571C73.2254 20.5617 73.1973 20.7997 73.2024 21.0708L72.7342 20.7638C72.7342 20.2061 72.7879 19.6996 72.8954 19.2442C73.008 18.7837 73.1666 18.3872 73.3712 18.0546C73.5759 17.7169 73.8241 17.4585 74.1157 17.2794C74.4074 17.0952 74.7348 17.0031 75.0981 17.0031C75.226 17.0031 75.3514 17.0108 75.4742 17.0261C75.6021 17.0415 75.7147 17.0645 75.8119 17.0952ZM76.1035 21.3932V21.232C76.1035 20.6231 76.1905 20.0629 76.3645 19.5512C76.5385 19.0344 76.7943 18.5867 77.132 18.2081C77.4697 17.8294 77.8867 17.5352 78.383 17.3255C78.8793 17.1106 79.4524 17.0031 80.1022 17.0031C80.7571 17.0031 81.3328 17.1106 81.8291 17.3255C82.3305 17.5352 82.7501 17.8294 83.0878 18.2081C83.4255 18.5867 83.6813 19.0344 83.8553 19.5512C84.0292 20.0629 84.1162 20.6231 84.1162 21.232V21.3932C84.1162 21.997 84.0292 22.5572 83.8553 23.074C83.6813 23.5857 83.4255 24.0334 83.0878 24.4171C82.7501 24.7958 82.3331 25.09 81.8368 25.2998C81.3404 25.5095 80.7674 25.6144 80.1176 25.6144C79.4677 25.6144 78.8921 25.5095 78.3907 25.2998C77.8893 25.09 77.4697 24.7958 77.132 24.4171C76.7943 24.0334 76.5385 23.5857 76.3645 23.074C76.1905 22.5572 76.1035 21.997 76.1035 21.3932ZM78.69 21.232V21.3932C78.69 21.7053 78.713 21.997 78.7591 22.2681C78.8051 22.5393 78.8819 22.7773 78.9893 22.9819C79.0968 23.1815 79.2426 23.3375 79.4268 23.4501C79.611 23.5627 79.8413 23.6189 80.1176 23.6189C80.3887 23.6189 80.6139 23.5627 80.793 23.4501C80.9772 23.3375 81.123 23.1815 81.2304 22.9819C81.3379 22.7773 81.4146 22.5393 81.4607 22.2681C81.5067 21.997 81.5298 21.7053 81.5298 21.3932V21.232C81.5298 20.9301 81.5067 20.6462 81.4607 20.3801C81.4146 20.1089 81.3379 19.871 81.2304 19.6663C81.123 19.4565 80.9772 19.2928 80.793 19.1751C80.6088 19.0574 80.3785 18.9986 80.1022 18.9986C79.831 18.9986 79.6033 19.0574 79.4191 19.1751C79.24 19.2928 79.0968 19.4565 78.9893 19.6663C78.8819 19.871 78.8051 20.1089 78.7591 20.3801C78.713 20.6462 78.69 20.9301 78.69 21.232ZM84.9451 21.3932V21.232C84.9451 20.6231 85.0321 20.0629 85.2061 19.5512C85.38 19.0344 85.6359 18.5867 85.9736 18.2081C86.3113 17.8294 86.7283 17.5352 87.2246 17.3255C87.7209 17.1106 88.294 17.0031 88.9438 17.0031C89.5987 17.0031 90.1743 17.1106 90.6707 17.3255C91.1721 17.5352 91.5917 17.8294 91.9294 18.2081C92.2671 18.5867 92.5229 19.0344 92.6969 19.5512C92.8708 20.0629 92.9578 20.6231 92.9578 21.232V21.3932C92.9578 21.997 92.8708 22.5572 92.6969 23.074C92.5229 23.5857 92.2671 24.0334 91.9294 24.4171C91.5917 24.7958 91.1747 25.09 90.6783 25.2998C90.182 25.5095 89.609 25.6144 88.9591 25.6144C88.3093 25.6144 87.7337 25.5095 87.2323 25.2998C86.7308 25.09 86.3113 24.7958 85.9736 24.4171C85.6359 24.0334 85.38 23.5857 85.2061 23.074C85.0321 22.5572 84.9451 21.997 84.9451 21.3932ZM87.5316 21.232V21.3932C87.5316 21.7053 87.5546 21.997 87.6007 22.2681C87.6467 22.5393 87.7235 22.7773 87.8309 22.9819C87.9384 23.1815 88.0842 23.3375 88.2684 23.4501C88.4526 23.5627 88.6828 23.6189 88.9591 23.6189C89.2303 23.6189 89.4555 23.5627 89.6345 23.4501C89.8187 23.3375 89.9646 23.1815 90.072 22.9819C90.1795 22.7773 90.2562 22.5393 90.3023 22.2681C90.3483 21.997 90.3713 21.7053 90.3713 21.3932V21.232C90.3713 20.9301 90.3483 20.6462 90.3023 20.3801C90.2562 20.1089 90.1795 19.871 90.072 19.6663C89.9646 19.4565 89.8187 19.2928 89.6345 19.1751C89.4503 19.0574 89.2201 18.9986 88.9438 18.9986C88.6726 18.9986 88.4449 19.0574 88.2607 19.1751C88.0816 19.2928 87.9384 19.4565 87.8309 19.6663C87.7235 19.871 87.6467 20.1089 87.6007 20.3801C87.5546 20.6462 87.5316 20.9301 87.5316 21.232ZM97.2788 25.4609H94.677V16.4812C94.677 15.8365 94.8075 15.2941 95.0684 14.8541C95.3294 14.4141 95.6978 14.0815 96.1736 13.8564C96.6495 13.6261 97.2123 13.511 97.8621 13.511C98.0873 13.511 98.2996 13.5263 98.4992 13.557C98.6987 13.5877 98.8931 13.6261 99.0825 13.6722L99.0901 15.5756C98.998 15.55 98.9034 15.5321 98.8062 15.5218C98.7089 15.5116 98.5887 15.5065 98.4454 15.5065C98.1947 15.5065 97.9824 15.5449 97.8084 15.6216C97.6344 15.6932 97.5014 15.8032 97.4093 15.9516C97.3223 16.0949 97.2788 16.2714 97.2788 16.4812V25.4609ZM98.8906 17.1566V18.9525H93.4797V17.1566H98.8906Z"
                    fill="white"
                  />
                </svg>
              </div>
              <nav className={clsx("mt-2 flex-1 space-y-1 px-2")}>
                <SidebarComponent />
              </nav>
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col md:pl-64 print:pl-0">
          <div className="sticky top-0 z-10 bg-gray-100 pt-1 pl-1 sm:pt-3 sm:pl-3 md:hidden print:hidden">
            <button
              type="button"
              className="-mt-0.5 -ml-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <MenuIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="flex justify-end">
            <div className={clsx("flex justify-start px-5 pt-5 print:hidden")}>
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                <Menu as="div" className="relative">
                  <Menu.Button className="-m-1.5 flex items-center p-1.5">
                    <span className="hidden lg:flex lg:items-center">
                      <span
                        className="ml-4 text-sm font-semibold capitalize leading-6 text-gray-800"
                        aria-hidden="true"
                      >
                        {!profile.tenantId
                          ? "Välj Företag"
                          : `Företag: ${
                              tenants?.find((x) =>
                                x.id.includes(profile?.tenantId || "")
                              )?.displayName || ""
                            }`}
                      </span>
                      <ChevronDownIcon
                        className="ml-2 h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2.5 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                      {tenants?.map((t: string, idx: number) => (
                        <Menu.Item key={idx}>
                          {({ active }) => (
                            <a
                              onClick={async () => {
                                setLoading(true);
                                const token = await executeCallable({
                                  // @ts-ignore
                                  newTenantId: t.id,
                                });

                                if (token?.data) {
                                  // @ts-ignore
                                  auth.tenantId = t.id;
                                  await signInWithCustomToken(
                                    auth,
                                    // @ts-ignore
                                    token.data
                                  );

                                  await router
                                    .replace("/dashboard")
                                    .then(() => router.reload());
                                  setLoading(false);
                                }
                              }}
                              className={classNames(
                                active ? "bg-gray-50 hover:cursor-pointer" : "",
                                "block px-3 py-1 text-sm leading-6 text-gray-900 capitalize"
                              )}
                            >
                              {
                                // @ts-ignore
                                t.displayName
                              }
                            </a>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>

            <div className="flex justify-end px-5 pt-5 print:hidden">
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                <button
                  type="button"
                  className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">View notifications</span>
                  <BellIcon
                    className="h-6 w-6"
                    color="gray"
                    aria-hidden="true"
                  />
                </button>

                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <Menu.Button className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>
                    <img
                      className="h-8 w-8 rounded-full bg-gray-50"
                      src={
                        user?.images && user.images.length > 0
                          ? user.images[0]
                          : publicUser?.images && publicUser.images.length > 0
                          ? publicUser.images[0]
                          : "https://research.cbc.osu.edu/sokolov.8/wp-content/uploads/2017/12/profile-icon-png-898.png"
                      }
                      alt=""
                    />
                    <span className="hidden lg:flex lg:items-center">
                      <span
                        className="ml-4 text-sm font-semibold leading-6 text-gray-800"
                        aria-hidden="true"
                      >
                        {profile?.displayName || profile?.email}
                      </span>
                      <ChevronDownIcon
                        className="ml-2 h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2.5 w-40 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            onClick={async () => {
                              router
                                .replace(`/public_users/${profile?.email}`)
                                .then(() => {
                                  router.reload();
                                });
                            }}
                            className={classNames(
                              active ? "bg-gray-50 hover:cursor-pointer" : "",
                              "block px-3 py-1 text-sm leading-6 text-gray-900"
                            )}
                          >
                            Profil
                          </a>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            onClick={() => {
                              router.push("/createTenant");
                            }}
                            className={classNames(
                              active ? "bg-gray-50 hover:cursor-pointer" : "",
                              "block px-3 py-1 text-sm leading-6 text-gray-900"
                            )}
                          >
                            Skapa organisation
                          </a>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            onClick={async () => {
                              await signOut();
                              return router
                                .push("/")
                                .then(() =>
                                  setTimeout(() => router.reload(), 500)
                                );
                            }}
                            className={classNames(
                              active ? "bg-gray-50 hover:cursor-pointer" : "",
                              "block px-3 py-1 text-sm leading-6 text-gray-900"
                            )}
                          >
                            Logga ut
                          </a>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
          <main className={`flex-1 bg-white ${props.noMargin ? "" : "m-10"}`}>
            {router.asPath.includes("/projects/") ? (
              <ProjectSideBar>{props.children}</ProjectSideBar>
            ) : (
              <>{props.children}</>
            )}
          </main>
        </div>
      </div>
      <Modal
        className="flex justify-center bg-transparent"
        open={loading}
        onClose={() => {}}
        hideButton={true}
      >
        <RotatingSquare
          height="80"
          width="80"
          color="blue"
          ariaLabel="three-dots-loading"
          visible={loading}
        />
      </Modal>
    </div>
  );
};
